import {
  Appointments,
  CustomerGuests,
  Customers,
  Users,
  WaiverRequests,
} from '@prisma/client';
import { isAfter } from 'date-fns';
import * as yup from 'yup';
import { DEFAULT_QUEUE_SETTINGS } from '../../constants/queue';

export const queueSettingsSchema = yup.object({
  enabled: yup.boolean().required(),
  allowArtistsToPull: yup.boolean().required(),
  collectArtistPreference: yup.boolean().required(),
  collectServiceDetails: yup.boolean().required(),
  waiverBehavior: yup.string().required(),
});

export const REASON_OPTIONS = ['COMPLETED', 'CANCELED', 'ARCHIVED'] as const;

export type RemoveFromQueueReason = (typeof REASON_OPTIONS)[number];

export type QueueSettingsPayload = yup.InferType<typeof queueSettingsSchema>;

export type QueueAppointment = Appointments & {
  customer: Customers & Users;
  customerGuest?: Pick<
    CustomerGuests,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'dob'
    | 'email'
    | 'medicalHistory'
    | 'relation'
  > | null;
  waiverRequest: WaiverRequests | null;
};

export const removeFromQueueSchema = yup.object({
  reason: yup.mixed<RemoveFromQueueReason>().oneOf(REASON_OPTIONS).required(),
  employeeId: yup.string().when('reason', {
    is: (reason: RemoveFromQueueReason) => reason === 'COMPLETED',
    then: schema => schema.required(),
    otherwise: schema => schema.nullable().optional().notRequired(),
  }),
  startDateTime: yup.date().when('reason', {
    is: (reason: RemoveFromQueueReason) => reason === 'COMPLETED',
    then: schema => schema.required(),
    otherwise: schema => schema.nullable().optional().notRequired(),
  }),
  endDateTime: yup.date().when('reason', {
    is: (reason: RemoveFromQueueReason) => reason === 'COMPLETED',
    // must be after startDateTime
    then: schema =>
      schema
        .test({
          test: (value, context) => {
            const { startDateTime } = context.parent;
            return value && isAfter(value, startDateTime);
          },
          message: 'End date must be after start date',
        })
        .required(),
    otherwise: schema => schema.nullable().optional().notRequired(),
  }),
});

export type RemoveFromQueuePayload = yup.InferType<
  typeof removeFromQueueSchema
>;

export type BusinessQueueSettings = typeof DEFAULT_QUEUE_SETTINGS;
